import {
  normalizePdrProfile,
  PDR_PROFILE_COMPLETE,
  PDR_PROFILE_SLIM,
  type PdrSchemaProfile,
} from "./pdrFields";
import type { ProjectData } from "./pdrFields";

const PREFS_KEY = "orion1_eval_playground_prefs";
const HISTORY_KEY = "orion1_eval_playground_history";
const STANDALONE_PDR_KEY = "orion1_eval_playground_standalone_pdr_v2";
/** Synthesis prompts edited in the eval sandbox, by kind; sent with runs, never saved to production. */
const PROMPT_SANDBOX_KEY = "orion1_eval_playground_prompt_sandbox";
/** @deprecated Per-project drafts; migrated to standalone on read */
const LEGACY_DRAFTS_KEY = "orion1_eval_playground_pdr_drafts";
const LEGACY_STANDALONE_KEY = "orion1_eval_playground_standalone_pdr";
const MAX_HISTORY = 50;

export type EvalResultSnapshot = {
  model: string;
  latency_ms: number;
  cost_estimate: number;
  generated_pdr: ProjectData | null;
  validation_passed: boolean;
  validation_errors?: string[];
  error?: string;
  token_usage?: { prompt_tokens?: number; completion_tokens?: number };
  /** Successive synthesis calls this model made / was due to make (absent before chaining). */
  calls_made?: number;
  calls_planned?: number;
  /** cost_estimate is what OpenRouter billed; false or absent means a catalog estimate. */
  cost_billed?: boolean;
};

/** A Phase 3 vendor cluster (target company + contractor bucket) as the picker lists it. */
export type EvalCluster = {
  bucket_id: string;
  cluster_id: string;
  label: string;
  target_company: string;
  contractor: string;
  document_count: number;
  project_count: number;
};

export type EvalClusterDocument = {
  evidence_id: string;
  title: string;
  url: string;
  chars: number;
  synthesized: boolean;
};

/** What a run's calls read of each document. */
export type EvalCoverage = {
  documents_total?: number;
  documents_read?: number;
  documents_partial?: number;
  documents_unread?: number;
  partial_evidence_ids?: string[];
  unread_evidence_ids?: string[];
  chars_evaluated?: number;
  chars_total?: number;
  calls_per_model?: number;
  batches_total?: number;
};

export type EvalEvidencePreview = {
  title?: string;
  url?: string;
  /** Vendor-cluster runs only. */
  cluster?: EvalCluster;
  documents?: { evidence_id: string; title: string; url: string }[];
  coverage?: EvalCoverage;
  /** Every prompt the run sent, by kind: "run" = the sandbox's edit, "production" = saved text. */
  prompts?: Record<string, { source: string; text: string }>;
};

export type SavedEvalRun = {
  id: string;
  saved_at: string;
  evidence_id: string;
  /** Empty when run used standalone eval PDR only. */
  project_id?: string;
  include_reasoning: boolean;
  models: string[];
  /** Sandbox PDR context document (markdown or JSON text) used for this run. */
  pdr_context?: string;
  pdr_schema_profile?: string;
  /** @deprecated Legacy JSON snapshot */
  existing_pdr?: ProjectData;
  evidence_preview?: EvalEvidencePreview;
  project_preview?: { project_name?: string; target_company?: string; vendor_name?: string };
  results: EvalResultSnapshot[];
};

export type StandalonePdrEntry = {
  json: string;
  updated_at: string;
  explicitly_saved?: boolean;
};

export type SandboxPdrStore = Record<PdrSchemaProfile, StandalonePdrEntry | null>;

export type EvalPlaygroundPrefs = {
  selectedModels: string[];
  /** Server default arena when selectedModels was saved (see restoredEvalModels). */
  defaultModels?: string[];
  /** Last vendor cluster (Phase 3 bucket id) picked for evaluation. */
  bucketId?: string;
  /** Background evaluation still running when the page was left; polled again on return. */
  runningJobId?: string;
  /** @deprecated Single-page input, replaced by bucketId */
  evidenceId?: string;
  includeReasoning: boolean;
  lastRunId: string;
  pdrSchemaProfile?: string;
  pdrCollapsed?: boolean;
  resultsFocusMode?: boolean;
  tableExpanded?: boolean;
  /** @deprecated No longer used — PDR is standalone */
  projectId?: string;
};

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota or private mode */
  }
}

function emptySandboxStore(): SandboxPdrStore {
  return { slim: null, complete: null };
}

function migrateLegacyStandalone(): SandboxPdrStore {
  const store = emptySandboxStore();
  const v2 = readJson<SandboxPdrStore | null>(STANDALONE_PDR_KEY, null);
  if (v2 && (v2.slim || v2.complete)) {
    return {
      slim: v2.slim ?? null,
      complete: v2.complete ?? null,
    };
  }
  const legacy = readJson<StandalonePdrEntry | null>(LEGACY_STANDALONE_KEY, null);
  if (legacy?.json?.trim()) {
    const prof: PdrSchemaProfile = legacy.json.includes("CHAPTER 0")
      ? PDR_PROFILE_COMPLETE
      : PDR_PROFILE_SLIM;
    store[prof] = legacy;
    writeJson(STANDALONE_PDR_KEY, store);
    return store;
  }
  const legacyDrafts = readJson<Record<string, { json: string; explicitly_saved?: boolean }>>(
    LEGACY_DRAFTS_KEY,
    {},
  );
  const saved = Object.entries(legacyDrafts).find(([, v]) => v.explicitly_saved && v.json?.trim());
  if (saved) {
    const [, v] = saved;
    const entry: StandalonePdrEntry = {
      json: v.json,
      updated_at: new Date().toISOString(),
      explicitly_saved: true,
    };
    store.slim = entry;
    writeJson(STANDALONE_PDR_KEY, store);
  }
  return store;
}

export function loadEvalSandboxPdrStore(): SandboxPdrStore {
  const raw = readJson<SandboxPdrStore | null>(STANDALONE_PDR_KEY, null);
  if (raw && (raw.slim || raw.complete)) {
    return {
      slim: raw.slim ?? null,
      complete: raw.complete ?? null,
    };
  }
  return migrateLegacyStandalone();
}

export function loadEvalPrefs(): Partial<EvalPlaygroundPrefs> {
  return readJson<Partial<EvalPlaygroundPrefs>>(PREFS_KEY, {});
}

export function saveEvalPrefs(prefs: EvalPlaygroundPrefs): void {
  writeJson(PREFS_KEY, prefs);
}

/**
 * The arena to open with. A saved selection still equal to the default it was saved under was
 * never customised, so it follows the current default; a customised one is kept. Prefs from
 * before defaultModels was tracked hold the retired three-model default, so they take the
 * current default once.
 */
export function restoredEvalModels(
  prefs: Partial<EvalPlaygroundPrefs>,
  defaults: string[],
): string[] {
  const saved = prefs.selectedModels ?? [];
  const savedDefaults = prefs.defaultModels;
  if (!saved.length || !savedDefaults) return defaults;
  const customised =
    saved.length !== savedDefaults.length || saved.some((m) => !savedDefaults.includes(m));
  return customised ? saved : defaults;
}

export function loadEvalHistory(): SavedEvalRun[] {
  const rows = readJson<SavedEvalRun[]>(HISTORY_KEY, []);
  return Array.isArray(rows) ? rows : [];
}

/** Replace local cache with server-backed history (keeps full runs when present). */
export function syncEvalHistoryCache(runs: SavedEvalRun[]): void {
  if (!runs.length) return;
  const byId = new Map(loadEvalHistory().map((r) => [r.id, r]));
  for (const run of runs) {
    const prev = byId.get(run.id);
    const merged: SavedEvalRun = {
      ...(prev || {}),
      ...run,
      results: run.results?.length ? run.results : prev?.results || [],
      pdr_context: run.pdr_context ?? prev?.pdr_context,
    };
    byId.set(run.id, merged);
  }
  const merged = [...byId.values()].sort(
    (a, b) => new Date(b.saved_at).getTime() - new Date(a.saved_at).getTime(),
  );
  writeJson(HISTORY_KEY, merged.slice(0, MAX_HISTORY));
}

export function upsertEvalRun(run: SavedEvalRun): void {
  const history = loadEvalHistory().filter((r) => r.id !== run.id);
  writeJson(HISTORY_KEY, [run, ...history].slice(0, MAX_HISTORY));
}

export function appendEvalRun(run: Omit<SavedEvalRun, "id" | "saved_at">): SavedEvalRun {
  const entry: SavedEvalRun = {
    ...run,
    id: crypto.randomUUID(),
    saved_at: new Date().toISOString(),
  };
  const history = [entry, ...loadEvalHistory()].slice(0, MAX_HISTORY);
  writeJson(HISTORY_KEY, history);
  return entry;
}

export function deleteEvalRun(runId: string): void {
  const history = loadEvalHistory().filter((r) => r.id !== runId);
  writeJson(HISTORY_KEY, history);
}

export function getEvalRun(runId: string): SavedEvalRun | undefined {
  return loadEvalHistory().find((r) => r.id === runId);
}

/** Eval-only PDR document for one schema profile. */
export function loadEvalStandalonePdrEntry(
  profile: PdrSchemaProfile = PDR_PROFILE_SLIM,
): StandalonePdrEntry | null {
  const prof = normalizePdrProfile(profile);
  return loadEvalSandboxPdrStore()[prof];
}

export function loadEvalStandalonePdr(profile?: PdrSchemaProfile): string | null {
  return loadEvalStandalonePdrEntry(profile)?.json ?? null;
}

export function saveEvalStandalonePdr(
  profile: PdrSchemaProfile,
  json: string,
  opts?: { explicitlySaved?: boolean },
): void {
  const prof = normalizePdrProfile(profile);
  const store = loadEvalSandboxPdrStore();
  const prev = store[prof];
  const entry: StandalonePdrEntry = {
    json,
    updated_at: new Date().toISOString(),
    explicitly_saved:
      opts?.explicitlySaved !== undefined ? opts.explicitlySaved : prev?.explicitly_saved,
  };
  store[prof] = entry;
  writeJson(STANDALONE_PDR_KEY, store);
}

export function sandboxTextsFromStore(
  store: SandboxPdrStore,
): Record<PdrSchemaProfile, string> {
  return {
    slim: store.slim?.json ?? "",
    complete: store.complete?.json ?? "",
  };
}

export function loadEvalPromptSandbox(): Record<string, string> {
  const value = readJson<Record<string, string>>(PROMPT_SANDBOX_KEY, {});
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

export function saveEvalPromptSandbox(prompts: Record<string, string>): void {
  writeJson(PROMPT_SANDBOX_KEY, prompts);
}
